import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { UserPlus, Edit, Coins, MoreVertical, Trash2, Eye, EyeOff, Search, X, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

// Roles staff can manually assign. "guest" is deliberately excluded — it is
// a self-service-only role created via the guest-login flow and can never
// be hand-assigned to a user from this UI.
const ALL_ROLES = [
  { value: 'client', label: 'Client' },
  { value: 'center_manager', label: 'Center Manager' },
  { value: 'cluster_manager', label: 'Cluster Manager' },
  { value: 'hub_manager', label: 'Hub Manager' },
  { value: 'sales', label: 'Sales' },
  { value: 'founders', label: 'Founders' },
  { value: 'finance_audit', label: 'Finance & Audit' },
  { value: 'landlord', label: 'Landlord' },
  { value: 'marketing_ops', label: 'Marketing Ops' },
  { value: 'bd_transactions', label: 'BD Transactions' },
  { value: 'admin', label: 'Admin' },
];

// Filter-only options — includes "guest" since guest rows do exist in the
// list, they're just excluded from the default view and non-assignable.
const FILTER_ROLES = [...ALL_ROLES, { value: 'guest', label: 'Guest' }];

export const UsersTab = ({
  users, currentUser, organizations, theme, api, creditsByEntity = {},
  onAddUser, onEditUser, onToggleActive, onDelete, onAddCredits, onRefresh
}) => {
  const { cardBg, cardBorder, hoverBg } = theme;
  const isAdmin = currentUser?.role === 'admin';
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [resettingId, setResettingId] = useState(null);

  const handleResetPassword = async (userId, userName) => {
    setResettingId(userId);
    try {
      await api.post('/auth/admin-reset-password', { user_id: userId });
      toast.success(`Password reset email sent for ${userName}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setResettingId(null);
    }
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.name || '-';
  };

  const filteredUsers = useMemo(() => {
    let result = users;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(u =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q) ||
        getOrgName(u.organization_id)?.toLowerCase().includes(q)
      );
    }
    if (roleFilter === 'all') {
      // Guests are transient self-service accounts — keep them out of the
      // default view so they don't dilute the main staff/customer list.
      result = result.filter(u => u.role !== 'guest');
    } else {
      result = result.filter(u => u.role === roleFilter);
    }
    return result;
  }, [users, searchQuery, roleFilter]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.patch(`/users/${userId}`, { role: newRole });
      toast.success(`Role updated to ${newRole.replace('_', ' ')}`);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    }
  };

  return (
    <Card className={`${cardBg} ${cardBorder}`} data-testid="users-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading">User Management</CardTitle>
        <Button className="bg-primary hover:bg-primary/90" size="sm" onClick={onAddUser} data-testid="add-user-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, phone, or organization..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8"
              data-testid="users-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px]" data-testid="users-role-filter">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {FILTER_ROLES.map(r => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground mb-3">{filteredUsers.length} of {users.length} users</p>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`border-b ${cardBorder}`}>
                <th className="text-left py-4 px-4 text-sm font-medium text-muted-foreground">Name</th>
                <th className="text-left py-4 px-4 text-sm font-medium text-muted-foreground">Email</th>
                <th className="text-left py-4 px-4 text-sm font-medium text-muted-foreground">Organization</th>
                <th className="text-left py-4 px-4 text-sm font-medium text-muted-foreground">Type</th>
                <th className="text-left py-4 px-4 text-sm font-medium text-muted-foreground">Role</th>
                <th className="text-right py-4 px-4 text-sm font-medium text-muted-foreground">Credits</th>
                <th className="text-left py-4 px-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left py-4 px-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">No users match your search</td></tr>
              ) : filteredUsers.map(u => (
                <tr key={u.id} className={`border-b ${cardBorder}/50 ${hoverBg} transition-colors`}>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary">{u.name?.charAt(0)}</span>
                      </div>
                      <span className="font-medium">{u.name}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-sm text-muted-foreground">{u.email}</td>
                  <td className="py-4 px-4 text-sm">{u.organization_id ? getOrgName(u.organization_id) : '-'}</td>
                  <td className="py-4 px-4"><Badge variant="outline" className={cardBorder}>{u.user_type}</Badge></td>
                  <td className="py-4 px-4">
                    {u.role === 'guest' ? (
                      <Badge className="bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700">Guest</Badge>
                    ) : isAdmin && u.id !== currentUser?.id ? (
                      <Select
                        value={u.role}
                        onValueChange={(newRole) => handleRoleChange(u.id, newRole)}
                      >
                        <SelectTrigger className="h-8 w-40 text-xs" data-testid={`role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ALL_ROLES.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {u.granted_roles?.length > 1 ? (
                          u.granted_roles.map(r => (
                            <Badge
                              key={r}
                              variant={r === u.role ? 'secondary' : 'outline'}
                              className="capitalize"
                            >
                              {r.replace('_', ' ')}
                            </Badge>
                          ))
                        ) : (
                          <Badge variant="secondary" className="capitalize">{u.role?.replace('_', ' ')}</Badge>
                        )}
                        {u.role === 'center_manager' && u.all_centers_access && (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] px-1.5">All Centers</Badge>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-4 px-4 text-right" data-testid={`user-credits-cell-${u.id}`}>
                    {creditsByEntity[u.id] ? (
                      <div className="text-right">
                        <div className="font-mono text-xs">
                          <span className="font-semibold text-blue-700">{Number(creditsByEntity[u.id].balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })}M</span>
                          <span className="text-muted-foreground mx-1">·</span>
                          <span className="font-semibold text-amber-700">{Number(creditsByEntity[u.id].topup_balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })}T</span>
                        </div>
                        {creditsByEntity[u.id].used_in_period > 0 && (
                          <div className="text-[10px] text-muted-foreground">{Number(creditsByEntity[u.id].used_in_period).toLocaleString('en-IN', { maximumFractionDigits: 0 })} used (30d)</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <Badge className={u.is_active ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30 border' : 'bg-red-500/20 text-red-500 border-red-500/30 border'}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="py-4 px-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEditUser(u)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                        {u.role !== 'guest' && (
                          <DropdownMenuItem onClick={() => handleResetPassword(u.id, u.name)} disabled={resettingId === u.id}>
                            <KeyRound className="w-4 h-4 mr-2" /> Reset Password
                          </DropdownMenuItem>
                        )}
                        {u.user_type === 'b2c' && u.role !== 'guest' && (
                          <DropdownMenuItem onClick={() => onAddCredits(u.id)}><Coins className="w-4 h-4 mr-2" /> Add Credits</DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => onToggleActive(u)}>
                          {u.is_active ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </DropdownMenuItem>
                        {u.id !== currentUser?.id && (
                          <DropdownMenuItem onClick={() => onDelete(u)} className="text-red-500 focus:text-red-500">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
